import httpClient from '../services/httpInterceptor';

export interface DownloadFileOptions {
  endpoint: string;
  filename: string;
  mimeType?: string;
  onProgress?: (progress: number) => void;
}

/**
 * Downloads a file from the API endpoint
 * @param options - Download configuration options
 * @returns Promise that resolves when download is complete
 */
export const downloadFile = async (options: DownloadFileOptions): Promise<void> => {
  const { endpoint, filename, mimeType = 'application/octet-stream', onProgress } = options;

  try {
    // Make API request with progress tracking if callback provided
    const response = await httpClient.get(endpoint, {
      responseType: 'blob',
      headers: {
        'Accept': mimeType,
      },
      onDownloadProgress: onProgress ? (progressEvent) => {
        if (progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      } : undefined,
    });

    // Validate response
    if (!response.data || response.data.size === 0) {
      throw new Error('File is empty or corrupted');
    }

    // Create blob URL from the response
    const blob = new Blob([response.data], { type: mimeType });
    const url = window.URL.createObjectURL(blob);

    // Create temporary download link
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    
    // Trigger download
    link.click();
    
    // Cleanup after a short delay to ensure download starts
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);

  } catch (error: any) {
    // Re-throw with more context
    throw new Error(error.response?.data?.message || error.message || 'Download failed');
  }
};

/**
 * Downloads a PDF file from the API endpoint
 * @param endpoint - API endpoint for the PDF
 * @param filename - Name for the downloaded file
 * @param onProgress - Optional progress callback
 * @returns Promise that resolves when download is complete
 */
export const downloadPDF = async (
  endpoint: string, 
  filename: string, 
  onProgress?: (progress: number) => void
): Promise<void> => {
  return downloadFile({
    endpoint,
    filename,
    mimeType: 'application/pdf',
    onProgress,
  });
};

/**
 * Downloads an Excel file from the API endpoint
 * @param endpoint - API endpoint for the Excel file
 * @param filename - Name for the downloaded file
 * @param onProgress - Optional progress callback
 * @returns Promise that resolves when download is complete
 */
export const downloadExcel = async (
  endpoint: string, 
  filename: string, 
  onProgress?: (progress: number) => void
): Promise<void> => {
  return downloadFile({
    endpoint,
    filename,
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    onProgress,
  });
};

/**
 * Downloads a CSV file from the API endpoint
 * @param endpoint - API endpoint for the CSV file
 * @param filename - Name for the downloaded file
 * @param onProgress - Optional progress callback
 * @returns Promise that resolves when download is complete
 */
export const downloadCSV = async (
  endpoint: string, 
  filename: string, 
  onProgress?: (progress: number) => void
): Promise<void> => {
  return downloadFile({
    endpoint,
    filename,
    mimeType: 'text/csv',
    onProgress,
  });
};